import type { CanvasJSONLikeObject } from '@doer/frontend-components/creativity-editor';
import { ICONS, type IconName } from './icons';

export interface TextIconOpts {
  /** Base para los pieceId: se generan `${pieceId}-text` y `${pieceId}-icon`. */
  pieceId: string;
  text: string;
  icon: IconName;
  iconHeight?: number;
  gap?: number;
  fontSize: number;
  fontFamily?: string;
  fontWeight?: number | 'normal' | 'bold';
  fill?: string;
  /** Centro del conjunto (texto + icono). */
  position: { left: number; top: number };
}

/**
 * Texto con un icono a la derecha, centrado como conjunto. Devuelve dos piezas:
 * un IText y una **imagen real de Fabric** anclada a la derecha del texto (`attachTo`).
 *
 * Usamos una Image real (no el icono de la pill): `enlivenObjects` espera a que la
 * imagen cargue y Fabric la renderiza nativamente, sin el repintado async frágil que
 * tiene el icono de pill en el montaje polimórfico.
 */
export function buildTextIcon(opts: TextIconOpts): CanvasJSONLikeObject[] {
  const def = ICONS[opts.icon];
  const scale = (opts.iconHeight ?? def.defaultHeight) / def.height;
  const iconWidth = def.width * scale;
  const gap = opts.gap ?? 12;

  const textLeft = opts.position.left - (gap + iconWidth) / 2;
  const textId = `${opts.pieceId}-text`;
  const iconId = `${opts.pieceId}-icon`;

  return [
    {
      pieceId: textId,
      type: 'IText',
      version: '7.1.0',
      text: opts.text,
      fontFamily: opts.fontFamily ?? 'VAG Rounded',
      fontSize: opts.fontSize,
      fontWeight: opts.fontWeight ?? 500,
      fontStyle: 'normal',
      lineHeight: 1,
      charSpacing: 0,
      textAlign: 'center',
      styles: [],
      fill: opts.fill ?? '#616365',
      originX: 'center',
      originY: 'center',
      left: textLeft,
      top: opts.position.top,
      selectable: true,
      evented: true,
      objectCaching: true
    },
    {
      pieceId: iconId,
      type: 'Image',
      version: '7.1.0',
      src: def.src,
      crossOrigin: 'anonymous',
      filters: [],
      originX: 'left',
      originY: 'center',
      left: textLeft,
      top: opts.position.top,
      width: def.width,
      height: def.height,
      scaleX: scale,
      scaleY: scale,
      attachTo: { pieceId: textId, side: 'right', gap },
      selectable: true,
      evented: true,
      objectCaching: true
    }
  ];
}
